/**
 * One place that knows how every cache entry is named. Invalidation from
 * the live-update handler is a prefix match against these, so a typo'd
 * inline key array — the usual React Query failure mode — cannot quietly
 * produce a cache entry that nothing ever invalidates.
 */
export const qk = {
  tenants: () => ['tenants'],
  tenant: (tenantId) => ['tenant', tenantId],
  // V-61. Who can sign in to this company. Its own key rather than a
  // slice of `tenant`, because a login being switched off must refresh
  // this list without refetching the settings form beside it.
  tenantUsers: (tenantId) => ['tenant-users', tenantId],
  dashboard: (tenantId) => ['dashboard', tenantId],
  orders: (tenantId, filters) => ['orders', tenantId, filters],
  activeOrders: (tenantId) => ['orders', tenantId, 'active'],
  // Realaus laiko ETA dispečerio sąraše. NOT under the `orders` prefix, and
  // that is deliberate: everything that touches an order invalidates that
  // whole prefix (see useOrderInvalidation), and an ETA does not go stale
  // because somebody edited a note — it goes stale on its own clock, which
  // is what its refetchInterval is for. Sharing the prefix would make every
  // order edit fire a fresh round of outbound routing calls.
  liveEta: (tenantId) => ['live-eta', tenantId],
  // Dispečerio krūvio balansas. The date is IN the key rather than a filter
  // over one answer: the server aggregates a whole day before it returns
  // anything, so yesterday and today are two different answers, not two
  // slices of one.
  loadBalance: (tenantId, date) => ['load-balance', tenantId, date],
  // Periodinių užsakymų šablonai. The options are in the key because
  // `due_today` is answered per date by the server, so two dates are two
  // answers rather than two views of one.
  orderTemplates: (tenantId, opts) => ['order-templates', tenantId, opts ?? {}],
  order: (orderId) => ['order', orderId],
  orderEvents: (orderId) => ['order', orderId, 'events'],
  // V-63. The stops made on one load. Under the `order` prefix so that
  // anything invalidating an order refreshes its stops with it.
  orderDwellRecords: (orderId) => ['order', orderId, 'dwell-records'],
  orderRouteHistory: (orderId) => ['order', orderId, 'route-history'],
  orderTruckRoute: (orderId) => ['order', orderId, 'truck-route'],
  // V-66. What the quarry's price list makes of this load, against
  // what its waybill charged. Under the `order` prefix so a corrected
  // waybill weight refreshes the comparison it feeds.
  orderPriceEstimate: (orderId) => ['order', orderId, 'price-estimate'],
  drivers: (tenantId, opts) => ['drivers', tenantId, opts ?? {}],
  quarries: (tenantId, opts) => ['quarries', tenantId, opts ?? {}],
  sites: (tenantId, opts) => ['sites', tenantId, opts ?? {}],
  materials: (tenantId, opts) => ['materials', tenantId, opts ?? {}],
  analytics: (tenantId, days) => ['analytics', tenantId, days],
  // V-67. The per-order rows behind those aggregates. The sort and the
  // page are IN the key, because each of them changes what the answer
  // is rather than filtering an answer already held — the server sorts
  // before it cuts the page.
  analyticsOrders: (tenantId, days, sort, limit, offset) =>
    ['analytics', tenantId, days, 'orders', sort, limit, offset],
  dwellStatus: (tenantId) => ['dwell-status', tenantId],
  setupStatus: (tenantId) => ['setup-status', tenantId],
  vehicles: (tenantId, filters) => ['vehicles', tenantId, filters ?? {}],
  defects: (tenantId, status) => ['vehicle-defects', tenantId, status ?? 'all'],
  notifications: (tenantId) => ['notifications', tenantId],
  clients: (tenantId, opts) => ['clients', tenantId, opts ?? {}],
  client: (clientId) => ['client', clientId],
  // V-86. What this client owes, asked at the two moments more work is
  // agreed. Under the `client` prefix so recording a payment refreshes the
  // warning that quotes it — which is true only because `client` is in
  // useInvoicing.js's INVOICE_CACHES. V-91: it was not, and this comment
  // described a mechanism that did not exist for a night.
  clientBalance: (clientId) => ['client', clientId, 'balance'],
  // V-87. Whether this quarry's price list has a rate for this material.
  // Declared HERE rather than inline at the call site, which is V-91's
  // other lesson: an ad-hoc key nobody can see from the mutation that
  // changes it is a key nobody invalidates. useDataAdmin.js's price
  // mutations clear it by its `quarryId` prefix.
  // Two factories, not one with an optional argument: a prefix built by
  // leaving the last argument off comes out as [..., undefined], which is a
  // three-element key that prefix-matches nothing. The mutation would then
  // invalidate silently and the warning would stay exactly as stale as
  // before. Caught by the test, not by reading.
  quarryPriceCheck: (quarryId, cargoType) => ['quarry-price-check', quarryId, cargoType],
  quarryPriceChecks: (quarryId) => ['quarry-price-check', quarryId],
  orderRequests: (tenantId, status) => ['order-requests', tenantId, status ?? 'pending_approval'],
  orderRequest: (requestId) => ['order-request', requestId],
  receivables: (tenantId) => ['receivables', tenantId],
  unbilled: (tenantId, olderThanDays) => ['unbilled', tenantId, olderThanDays],
  worklist: (tenantId) => ['worklist', tenantId],
  downtime: (tenantId, since, until) => ['downtime', tenantId, since ?? null, until ?? null],
  routeDeviation: (tenantId, since, until) => ['route-deviation', tenantId, since ?? null, until ?? null],
  fuelReport: (tenantId, since, until) => ['fuel-report', tenantId, since ?? null, until ?? null],
  fuelFills: (tenantId, vehicleId) => ['fuel-fills', tenantId, vehicleId ?? null],
  servicePlans: (tenantId) => ['service-plans', tenantId],
  serviceDue: (tenantId) => ['service-due', tenantId],
  // V-60. The records themselves, which nothing in the console read.
  // Keyed on the vehicle filter as well as the tenant, because the
  // panel's picker changes what the answer IS rather than filtering an
  // answer it already has.
  serviceRecords: (tenantId, vehicleId, limit, offset) =>
    ['service-records', tenantId, vehicleId || null, limit, offset],
  serviceOnTime: (tenantId, since, until) => ['service-on-time', tenantId, since ?? null, until ?? null],
  monthClose: (tenantId, month) => ['month-close', tenantId, month ?? null],
  standingTime: (tenantId, month) => ['standing-time', tenantId, month ?? null],
  // The B2B portal's five stars, rolled up per month. The WINDOW is in the
  // key because changing it asks a different question — a twelve-month
  // trend and a three-month one are not the same answer filtered — and so
  // is the client filter, for the reason serviceRecords' vehicle id is.
  clientSatisfaction: (tenantId, months, clientId) =>
    ['client-satisfaction', tenantId, months ?? null, clientId ?? null],
  driverDays: (tenantId, since, until) => ['driver-days', tenantId, since ?? null, until ?? null],
  driverPayroll: (tenantId, since, until) =>
    ['driver-payroll', tenantId, since ?? null, until ?? null],
  // The PERIOD is in the key, not a filter over one cached answer:
  // 'last_month' and 'month' are different questions about different
  // sets of loads, and one entry serving both would put this month's
  // figures under last month's heading.
  managerSummary: (tenantId, period) => ['manager-summary', tenantId, period ?? 'month'],
  planVsActual: (tenantId, days) => ['route-plan-actual', tenantId, days],
  // Every input is in the key, because changing any of them asks a
  // different question about a different job — and the answer is what a
  // dispatcher is about to agree a price on.
  quote: (tenantId, quarryId, siteId, cargoType, tons, pricePerTon) =>
    ['quote', tenantId, quarryId, siteId, cargoType, tons, pricePerTon ?? null],
  invoices: (tenantId, filters) => ['invoices', tenantId, filters ?? null],
  // The column layouts the bookkeeper's export can be written in. Its own
  // key rather than a slice of `invoices`: it depends on no invoice and on
  // no tenant data at all, so an issued invoice must not refetch it — and
  // it must not be dropped by INVOICE_CACHES either, which is why it is not
  // named under that prefix.
  invoiceExportProfiles: (tenantId) => ['invoice-export-profiles', tenantId],
  // The numbering/VAT self-check over one period. The RANGE is in the key
  // because it decides what the answer is rather than filtering an answer
  // already held — the server reads only that window, and a gap at its edge
  // belongs to the neighbouring month's check.
  invoiceCompliance: (tenantId, from, to) => ['invoice-compliance', tenantId, from, to],
  invoice: (invoiceId) => ['invoice', invoiceId],
  // The client and the cut-off are part of the key, not just of the URL: a
  // draft is a proposal about one client's money, and a cached draft served
  // under a key that ignored the cut-off would offer August's lines under a
  // September heading.
  invoiceDraft: (tenantId, clientId, until) => ['invoice-draft', tenantId, clientId ?? null, until || null],
  leaves: (tenantId, range) => ['leaves', tenantId, range ?? {}],
};
